import './buffer-polyfill';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { register } from 'swiper/element/bundle';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeJa from '@angular/common/locales/ja';
import localeZh from '@angular/common/locales/zh';
import localeKo from '@angular/common/locales/ko';
import localeNl from '@angular/common/locales/nl';
import localeSv from '@angular/common/locales/sv';
import localeDa from '@angular/common/locales/da';
import localeNb from '@angular/common/locales/nb';
import localeFi from '@angular/common/locales/fi';
import localePl from '@angular/common/locales/pl';
import localeAr from '@angular/common/locales/ar';
import { environment } from './environments/environment';
import { setupMlog } from './app/_services/logger';

// ---------------------------------------------------------------------------
// Global chunk-load error handler
//
// After a new build is deployed, stale tabs may try to lazy-load chunk files
// whose hashes no longer exist on Cloudflare Pages. The CDN returns index.html
// (SPA fallback) instead of the JS file, which produces:
//   "Failed to load module script: MIME type text/html"
//   "TypeError: Failed to fetch dynamically imported module"
//
// We catch these unhandled promise rejections globally and force a one-time
// full page reload so the browser fetches the new index.html and fresh chunks.
// sessionStorage prevents an infinite reload loop if the new build also fails.
// ---------------------------------------------------------------------------
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  const msg = (error as TypeError).message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed')
  );
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason;
  if (!isChunkLoadError(error)) return;

  const RELOAD_KEY = 'chunk_reload_attempted';
  if (sessionStorage.getItem(RELOAD_KEY)) {
    // Already reloaded once — do not loop.
    console.error('[ChunkLoad] Chunk load failed after reload, not retrying.', error);
    return;
  }

  console.warn('[ChunkLoad] Stale chunk detected, hard-reloading to fetch fresh build.');
  sessionStorage.setItem(RELOAD_KEY, '1');
  // Use cache-busting query param to force the browser to bypass its HTTP cache.
  // A plain window.location.reload() reuses cached chunks, which doesn't fix stale Vite deps.
  window.location.href = window.location.pathname + '?_cb=' + Date.now();
});

// Initialize mlog console API for runtime log control
setupMlog();

registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeEs);
registerLocaleData(localeIt);
registerLocaleData(localePt);
registerLocaleData(localeJa);
registerLocaleData(localeZh);
registerLocaleData(localeKo);
registerLocaleData(localeNl);
registerLocaleData(localeSv);
registerLocaleData(localeDa);
registerLocaleData(localeNb);
registerLocaleData(localeFi);
registerLocaleData(localePl);
registerLocaleData(localeAr);

register(); // Register Swiper custom elements

// Filter out noisy third-party library logs in non-prod environments
if (environment.env !== 'prod') {
  const originalConsoleLog = console.log.bind(console);
  console.log = (...args: unknown[]) => {
    // Filter out ngx-indexed-db connection close logs
    if (args.length > 0 && typeof args[0] === 'string') {
      if (args[0].includes('Database connection closed')) return;
    }
    originalConsoleLog(...args);
  };
}

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));
